import React from "react";

/* The Soma Lixir seal — a siren silhouette rising from a scallop shell, framed
   in a deep-sea ring. Hand-drawn-feeling, single-color SVG. */
export function SomaSeal({ size = 88, color = "currentColor", strokeWidth = 1.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* outer rings — sea trim */}
      <circle cx="50" cy="50" r="46" opacity="0.45" />
      <circle cx="50" cy="50" r="44.5" opacity="0.85" strokeDasharray="0.5 2.5" />

      {/* small stars / bubbles around the rim */}
      <circle cx="50" cy="9" r="0.9" fill={color} />
      <circle cx="91" cy="50" r="0.9" fill={color} />
      <circle cx="50" cy="91" r="0.9" fill={color} />
      <circle cx="9"  cy="50" r="0.9" fill={color} />

      {/* scallop shell at the base — ribbed half-fan */}
      <path d="M22 72 Q50 96 78 72" />
      <path d="M22 72 Q50 86 78 72" opacity="0.55" />
      {/* shell ribs radiating from hinge (50, 72) */}
      {Array.from({ length: 9 }).map((_, i) => {
        const t = i / 8;           // 0..1
        const angle = Math.PI * (1 + t); // sweep from PI to 2PI (lower half)
        const r1 = 4;
        const r2 = 22;
        const x1 = 50 + Math.cos(angle) * r1;
        const y1 = 72 + Math.sin(angle) * r1;
        const x2 = 50 + Math.cos(angle) * r2;
        const y2 = 72 + Math.sin(angle) * r2;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} opacity="0.55" />;
      })}
      {/* hinge dot */}
      <circle cx="50" cy="72" r="1.2" fill={color} />

      {/* siren silhouette rising from the shell — head, flowing hair, twin fish-tail */}
      {/* head */}
      <circle cx="50" cy="34" r="5" />
      {/* hair flowing to both sides */}
      <path d="M45 34 Q38 38 36 46 Q35 53 38 60" opacity="0.85" />
      <path d="M55 34 Q62 38 64 46 Q65 53 62 60" opacity="0.85" />
      <path d="M42 36 Q33 44 31 56" opacity="0.5" />
      <path d="M58 36 Q67 44 69 56" opacity="0.5" />

      {/* torso — narrow, suggesting figure */}
      <path d="M46 40 Q44 50 44 60" />
      <path d="M54 40 Q56 50 56 60" />

      {/* the twin tail splits at the waist (Starbucks-siren shape, more abstract) */}
      <path d="M44 60 Q38 64 36 70" />
      <path d="M56 60 Q62 64 64 70" />
      {/* tail fins curling outward */}
      <path d="M36 70 Q30 70 28 66 Q31 71 34 73" />
      <path d="M64 70 Q70 70 72 66 Q69 71 66 73" />

      {/* tiny crown — three soft points */}
      <path d="M46 28 L50 24 L54 28" opacity="0.7" />
      <circle cx="50" cy="23" r="0.9" fill={color} />
    </svg>
  );
}

/* Small decorative divider — a wave & shell mark with two side rules */
export function SunDivider({ width = 120, color = "var(--terracotta-soft)" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width, margin: "20px auto" }}>
      <span style={{ flex: 1, height: 1, background: color, opacity: 0.5 }} />
      <svg width="22" height="14" viewBox="0 0 32 18" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        {/* a small wave */}
        <path d="M2 11 Q6 4 10 11 T18 11 T26 11 T30 11" />
        {/* a tiny bubble above */}
        <circle cx="16" cy="3" r="1.4" />
      </svg>
      <span style={{ flex: 1, height: 1, background: color, opacity: 0.5 }} />
    </div>
  );
}

/* Hand-drawn line-art icons for the 7 lens cards */
export function LensIcon({ name, size = 44, color = "var(--terracotta-soft)" }) {
  const props = { width: size, height: size, viewBox: "0 0 64 64", fill: "none", stroke: color, strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "relationships":
      // Two crescents leaning into each other
      return (
        <svg {...props}>
          <path d="M24 20 Q14 32 24 44" />
          <path d="M40 20 Q50 32 40 44" />
          <circle cx="32" cy="32" r="2" fill={color} />
        </svg>
      );
    case "work_and_purpose":
      // Compass-rose star
      return (
        <svg {...props}>
          <path d="M32 12 L34 30 L52 32 L34 34 L32 52 L30 34 L12 32 L30 30 Z" />
          <circle cx="32" cy="32" r="2.5" />
        </svg>
      );
    case "inner_state":
      // Eye inside arch
      return (
        <svg {...props}>
          <path d="M14 48 L14 30 Q14 14 32 14 Q50 14 50 30 L50 48" />
          <path d="M20 36 Q32 28 44 36 Q32 44 20 36 Z" />
          <circle cx="32" cy="36" r="2" fill={color} />
        </svg>
      );
    case "change_and_transition":
      // Threshold arrow through arch
      return (
        <svg {...props}>
          <path d="M16 44 L16 30 Q16 18 32 18 Q48 18 48 30 L48 44" opacity="0.55" />
          <path d="M18 38 L46 38" />
          <path d="M40 32 L46 38 L40 44" />
        </svg>
      );
    case "rest_and_restoration":
      // Three concentric ripples
      return (
        <svg {...props}>
          <path d="M16 36 Q24 30 32 36 Q40 42 48 36" />
          <path d="M14 28 Q24 22 32 28 Q40 34 50 28" opacity="0.75" />
          <path d="M14 44 Q24 38 32 44 Q40 50 50 44" opacity="0.55" />
        </svg>
      );
    case "creativity":
      // Sun with rays
      return (
        <svg {...props}>
          <circle cx="32" cy="32" r="7" />
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            const r1 = 12, r2 = i % 2 === 0 ? 20 : 16;
            return <line key={i} x1={32 + Math.cos(a) * r1} y1={32 + Math.sin(a) * r1} x2={32 + Math.cos(a) * r2} y2={32 + Math.sin(a) * r2} />;
          })}
        </svg>
      );
    case "decision":
      // Fork in the road
      return (
        <svg {...props}>
          <path d="M32 50 L32 36" />
          <path d="M32 36 Q32 28 22 22" />
          <path d="M32 36 Q32 28 42 22" />
          <circle cx="22" cy="20" r="2" />
          <circle cx="42" cy="20" r="2" />
        </svg>
      );
    default:
      return null;
  }
}
